import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Field, useField } from 'react-final-form';
import RadioButtonField from './RadioButtonField';
import { RadioGroup } from 'ncoded-component-library';
import { ExtractPropTypes } from 'types';
import validations from 'validations';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';
import utils from 'utils';
import { useTranslation } from 'react-i18next';

import './RadioGroupField.styles.scss';

type RadioGroupProps = ExtractPropTypes<typeof RadioGroup>;

type RadioGroupFieldProps<T> = {
  styled?: boolean;
  noValidate?: boolean;
  noShowErr?: boolean;
  disabled?: boolean;
  description?: string;
  customOnChange?: (value: T) => void;
} & RadioGroupProps &
  FieldWrapperProps;

export type RadioOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  enabled?: boolean;
  tooltip?: React.ReactNode;
};

function RadioGroupField<T extends string>(props: RadioGroupFieldProps<T>) {
  const {
    className,
    options,
    name,
    direction,
    label,
    labelChildren,
    required,
    description,
    styled,
    disabled,
    noShowErr = false,
    noValidate = false,
    customOnChange,
    ...rest
  } = props;

  const {
    meta: { error, touched },
  } = useField(name);

  const { t } = useTranslation();

  const hasError = useMemo(
    () => !noShowErr && touched && error,
    [noShowErr, error, touched],
  );

  const classes = classNames('radio-group-field', className, {
    'radio-group-field--error': hasError,
  });
  const containerClasses = classNames('radio-group-field__button-container', {
    'radio-group-field__button-container--as-row': direction === 'row',
  });

  const renderOptions = useMemo(
    () =>
      options.map((option: RadioOption<string>, index) => (
        <Field
          key={option.value}
          name={name}
          label={option.label}
          value={option.value}
          validate={!noValidate ? validations.required() : utils.noop()}
          type="radio"
          styled={styled}
          disabled={disabled}
          {...rest}
          component={RadioButtonField}
          customOnChange={customOnChange}
        />
      )),
    [disabled, name, noValidate, options, rest, styled, customOnChange],
  );

  return (
    <FieldWrapper
      className={classes}
      error={t(error)}
      name={name}
      hasError={hasError}
      label={label}
      labelChildren={labelChildren}
      required={required}
      description={description}
    >
      <div className={containerClasses}>{renderOptions}</div>
    </FieldWrapper>
  );
}

export default RadioGroupField;
